/**
 * LocationChipInput — chip input with autocomplete dropdown for locations.
 *
 * Offers US states, major metros, and countries as suggestions.
 * Users can still type custom text and press Enter to add it.
 */

import { useState, useRef, useEffect } from 'react'
import { X, MapPin } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { FieldHint } from '@/components/field-hint'

// ---- Location data ----

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia',
] as const

const MAJOR_METROS = [
  'New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX',
  'Phoenix, AZ', 'San Francisco, CA', 'San Diego, CA', 'Dallas, TX',
  'Austin, TX', 'Denver, CO', 'Seattle, WA', 'Boston, MA',
  'Atlanta, GA', 'Miami, FL', 'Minneapolis, MN', 'Portland, OR',
  'Nashville, TN', 'Charlotte, NC', 'Raleigh, NC', 'Salt Lake City, UT',
  'San Jose, CA', 'Detroit, MI', 'Philadelphia, PA',
] as const

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia',
  'Germany', 'France', 'Netherlands', 'India', 'Singapore',
  'United Arab Emirates', 'Israel',
] as const

type LocationGroup = { readonly label: string; readonly items: readonly string[] }

const LOCATION_GROUPS: readonly LocationGroup[] = [
  { label: 'Countries', items: COUNTRIES },
  { label: 'US States', items: US_STATES },
  { label: 'Major Metros', items: MAJOR_METROS },
]

// ---- Component ----

export type LocationChipInputProps = {
  readonly label: string
  readonly placeholder?: string
  readonly chips: readonly string[]
  readonly onChange: (chips: readonly string[]) => void
  readonly disabled?: boolean
  readonly hint?: string
}

export function LocationChipInput({
  label,
  placeholder = 'Search locations...',
  chips,
  onChange,
  disabled,
  hint,
}: LocationChipInputProps) {
  const [inputVal, setInputVal] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const query = inputVal.toLowerCase().trim()

  // Filter suggestions: match against query, exclude already-selected chips
  const filteredGroups = LOCATION_GROUPS.map((group) => ({
    label: group.label,
    items: group.items.filter(
      (item) =>
        item.toLowerCase().includes(query) && !chips.includes(item),
    ),
  })).filter((g) => g.items.length > 0)

  const flatSuggestions = filteredGroups.flatMap((g) => g.items)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function addChip(value: string) {
    const trimmed = value.trim()
    if (!trimmed || chips.includes(trimmed)) return
    onChange([...chips, trimmed])
    setInputVal('')
    setHighlightIndex(-1)
  }

  function removeChip(chip: string) {
    onChange(chips.filter((c) => c !== chip))
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setIsOpen(true)
      setHighlightIndex((prev) => Math.min(prev + 1, flatSuggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightIndex >= 0 && highlightIndex < flatSuggestions.length) {
        // Add the highlighted suggestion
        addChip(flatSuggestions[highlightIndex]!)
      } else if (flatSuggestions.length > 0) {
        // No highlight but suggestions exist — auto-select the first match
        addChip(flatSuggestions[0]!)
      }
      // If no suggestions match, do nothing — prevents typos like "connectuicut"
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setHighlightIndex(-1)
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputVal(e.target.value)
    setIsOpen(true)
    setHighlightIndex(-1)
  }

  const showDropdown = isOpen && query.length > 0
  const noMatches = showDropdown && flatSuggestions.length === 0

  return (
    <div className="space-y-2" ref={containerRef}>
      <div className="flex items-center gap-1">
        <Label>{label}</Label>
        {hint && <FieldHint label={`What is ${label}?`}>{hint}</FieldHint>}
      </div>
      <div className="flex flex-wrap gap-1.5 rounded-md border border-input bg-transparent p-2 min-h-[40px]">
        {chips.map((chip) => (
          <Badge key={chip} variant="secondary" className="gap-1 pr-1">
            <MapPin className="h-3 w-3" />
            {chip}
            <button
              type="button"
              onClick={() => removeChip(chip)}
              disabled={disabled}
              className="rounded-full hover:bg-muted-foreground/20 p-0.5"
              aria-label={`Remove ${chip}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <input
          ref={inputRef}
          className="flex-1 min-w-[120px] bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed"
          placeholder={chips.length === 0 ? placeholder : 'Add more...'}
          value={inputVal}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputVal.trim() && setIsOpen(true)}
          disabled={disabled}
          autoComplete="off"
        />
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="relative">
          <div className="absolute z-50 w-full max-h-60 overflow-y-auto rounded-md border bg-popover shadow-md">
            {noMatches ? (
              <div className="px-3 py-3 text-sm text-muted-foreground text-center">
                No matching locations for "{inputVal}"
              </div>
            ) : (
              filteredGroups.map((group) => (
                <div key={group.label}>
                  <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/50">
                    {group.label}
                  </div>
                  {group.items.map((item) => {
                    const flatIdx = flatSuggestions.indexOf(item)
                    const isHighlighted = flatIdx === highlightIndex
                    return (
                      <button
                        key={item}
                        type="button"
                        className={`w-full text-left px-3 py-1.5 text-sm cursor-pointer ${
                          isHighlighted
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-muted/50'
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault() // prevent blur before click fires
                          addChip(item)
                        }}
                        onMouseEnter={() => setHighlightIndex(flatIdx)}
                      >
                        {item}
                      </button>
                    )
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Type to search locations, then select from the list
      </p>
    </div>
  )
}
